/**
 * Shared Sentry configuration consumed by instrumentation-client.ts
 * (the only runtime — this is a client-only static site).
 */

import type * as Sentry from "@sentry/nextjs";
import { getPublicEnv } from "@/lib/config/public-env";

type SentryInitOptions = NonNullable<Parameters<typeof Sentry.init>[0]>;

/**
 * Returns the Sentry.init() options for the client runtime.
 * Validates the trace sample rate env var and disables Sentry when DSN is absent
 * or APP_ENV is explicitly "development". When APP_ENV is unset, Sentry is
 * enabled (if DSN is present) with environment defaulting to "development".
 */
export function getSharedSentryOptions(): Pick<
  SentryInitOptions,
  | "dsn"
  | "environment"
  | "enabled"
  | "tracesSampleRate"
  | "enableLogs"
  | "sendDefaultPii"
> {
  const dsn = getPublicEnv("SENTRY_DSN");
  const rawRate = getPublicEnv("SENTRY_TRACES_SAMPLE_RATE");
  const parsed = parseFloat(rawRate ?? "0");
  const tracesSampleRate =
    isNaN(parsed) || parsed < 0 || parsed > 1 ? 0 : parsed;

  // Warn only when an explicit env value was set but fell outside the valid 0.0-1.0 range
  if (rawRate && tracesSampleRate !== parsed) {
    // console.error used intentionally — runs before Sentry.init() completes
    console.error(
      `[Sentry] Invalid SENTRY_TRACES_SAMPLE_RATE="${rawRate}". Must be 0.0–1.0. Defaulting to 0.`
    );
  }

  const appEnv = getPublicEnv("APP_ENV");

  return {
    dsn,
    environment: appEnv || "development",
    enabled: !!dsn && appEnv !== "development",
    tracesSampleRate,
    enableLogs: true,
    // Privacy policy commitment: error reports carry technical data only.
    // No IP addresses / default user context.
    sendDefaultPii: false,
  };
}
